import { useEffect, useMemo, useState, type MouseEvent } from "react"

import { ChartFigure } from "@coval/ui/chart-figure"
import { ChartScatter } from "lucide-react"
import {
  CartesianGrid,
  Cell,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts"

import { DIVIDER_STROKE } from "@/components/charts/chartTheme"

import type { ComparisonMetricRecordsById, ComparisonPlotProps } from "../types"
import {
  buildScatterDataset,
  type ScatterAxis,
  type ScatterPoint,
} from "../utils/buildScatterDataset"
import { alternatingBands, categoryDividers } from "../utils/categoryBands"
import { buildLegendEntries } from "../utils/legendPayload"
import { findScatterOutliers } from "../utils/outliers"
import { resolveScale } from "../utils/resolveScale"
import { categoryJitter } from "../utils/scatterJitter"
import { computeTrendLine } from "../utils/trendLine"
import { ComparisonTooltip } from "./ComparisonTooltip"
import { buildScatterFigure } from "./ScatterPlotFigure"

// Lane fill for alternating categorical bands — the divider hue at low opacity.
const BAND_FILL = DIVIDER_STROKE
const BAND_FILL_OPACITY = 0.5

const EMPTY_OUTLIER_IDS: ReadonlySet<string> = new Set()

// Footer count. Reports the kept (non-outlier) count plus how many were set
// aside, but only when the filter is on and actually removed some — otherwise
// just the count, so a clean sample doesn't read "N points, 0 outliers".
function formatPointCount(
  total: number,
  outliers: number,
  excludeOutliers: boolean,
): string {
  const kept = excludeOutliers ? total - outliers : total
  const keptLabel = kept === 1 ? "1 point" : `${kept} points`
  if (!excludeOutliers || outliers === 0) {
    return keptLabel
  }
  const outlierLabel = outliers === 1 ? "1 outlier" : `${outliers} outliers`
  return `${keptLabel}, ${outlierLabel}`
}

interface ScatterClickNode {
  payload?: ScatterPoint
}

function metricLabel(
  metricRecordsById: ComparisonMetricRecordsById,
  metricId: string,
): string {
  const record = metricRecordsById?.[metricId]
  return record?.metric_display_name || record?.metric_name || metricId
}

/**
 * A categorical axis plots integer category indices, so pin one tick per
 * category and render its label; numeric axes keep Recharts' defaults.
 */
function categoricalAxisProps(axis: ScatterAxis) {
  if (axis.kind !== "categorical") {
    return {}
  }
  return {
    domain: [-0.5, axis.categories.length - 0.5] as [number, number],
    ticks: axis.categories.map((_, index) => index),
    tickFormatter: (value: number) => axis.categories[value] ?? "",
    allowDecimals: false,
  }
}

function stopButtonClick(event: MouseEvent<HTMLElement>) {
  if (event.target instanceof Element && event.target.closest("button")) {
    event.stopPropagation()
  }
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex h-72 flex-col items-center justify-center gap-2 text-coval-text-muted">
      <ChartScatter className="h-6 w-6" aria-hidden="true" />
      <span className="text-sm">{message}</span>
    </div>
  )
}

export function ScatterPlot({
  config,
  runRows,
  compareBy,
  metricRecordsById,
  onPointClick,
  interactive,
}: ComparisonPlotProps) {
  const {
    xAxisMetricId,
    yAxisMetricId,
    zAxisMetricId,
    xAxisScale,
    yAxisScale,
    zAxisScale,
    xAxisScaleExponent,
    yAxisScaleExponent,
    zAxisScaleExponent,
  } = config
  const { analyticsPoints, renderPoints, xAxis, yAxis } = useMemo(
    () =>
      buildScatterDataset(
        runRows,
        xAxisMetricId,
        yAxisMetricId,
        compareBy,
        metricRecordsById,
        zAxisMetricId,
      ),
    [
      runRows,
      xAxisMetricId,
      yAxisMetricId,
      compareBy,
      metricRecordsById,
      zAxisMetricId,
    ],
  )

  // Tukey-IQR outliers (union across the numeric axes); empty unless the toggle
  // is on. Excluded points drop out of the view, the axis fit, and the trend
  // fit, so the remaining cluster isn't compressed by a far-off value. Run
  // over analyticsPoints so a multi-tag simulation counts exactly once in
  // the fences rather than N times.
  const outlierIds = useMemo(
    () =>
      config.excludeOutliers
        ? findScatterOutliers(
            analyticsPoints,
            {
              x: xAxis.kind === "numeric",
              y: yAxis.kind === "numeric",
            },
            config.outlierIqrMultiplier,
          )
        : EMPTY_OUTLIER_IDS,
    [
      config.excludeOutliers,
      config.outlierIqrMultiplier,
      analyticsPoints,
      xAxis.kind,
      yAxis.kind,
    ],
  )

  // Analytics view (one entry per simulation) with outliers dropped. Feeds the
  // trend fit and the footer count so both weight each simulation once.
  const visibleAnalyticsPoints = useMemo(
    () =>
      outlierIds.size
        ? analyticsPoints.filter(
            (point) => !outlierIds.has(point.simulationOutputId),
          )
        : analyticsPoints,
    [analyticsPoints, outlierIds],
  )

  // Render view (one entry per membership) with outliers dropped — if a sim's
  // coordinates are outliers, every one of its per-group entries drops so the
  // plot and analytics stay in sync.
  const visibleRenderPoints = useMemo(
    () =>
      outlierIds.size
        ? renderPoints.filter(
            (point) => !outlierIds.has(point.simulationOutputId),
          )
        : renderPoints,
    [renderPoints, outlierIds],
  )

  // Memoize so log/pow keep a stable d3 scale instance across renders; a fresh
  // reference each render makes Recharts re-initialise the axis (tick jitter).
  const xScale = useMemo(
    () => resolveScale(xAxisScale, xAxisScaleExponent),
    [xAxisScale, xAxisScaleExponent],
  )
  const yScale = useMemo(
    () => resolveScale(yAxisScale, yAxisScaleExponent),
    [yAxisScale, yAxisScaleExponent],
  )
  const zScale = useMemo(
    () => resolveScale(zAxisScale, zAxisScaleExponent),
    [zAxisScale, zAxisScaleExponent],
  )

  // Pin the bubble-size domain to the full dataset so a point's bubble size does
  // not shift when the outlier filter drops an unrelated z-extreme point.
  // Iterates analyticsPoints (one per sim) so a multi-tag row doesn't skew
  // the domain by contributing its z value multiple times.
  const zDomain = useMemo<[number, number] | undefined>(() => {
    if (!zAxisMetricId) {
      return undefined
    }
    let min = Infinity
    let max = -Infinity
    for (const point of analyticsPoints) {
      if (point.z != null && Number.isFinite(point.z)) {
        min = Math.min(min, point.z)
        max = Math.max(max, point.z)
      }
    }
    return min < max ? [min, max] : undefined
  }, [zAxisMetricId, analyticsPoints])

  const [trendHovered, setTrendHovered] = useState(false)

  // Legend entries only exist while a Compare By is active; a stale saved
  // showLegend with Compare By off renders nothing. Sourced from the full
  // renderPoints (not the visible slice) so the legend documents the Compare
  // By grouping — including groups whose only rows the outlier filter hid.
  const legendEntries = useMemo(
    () =>
      config.showLegend && compareBy ? buildLegendEntries(renderPoints) : [],
    [config.showLegend, compareBy, renderPoints],
  )

  // Spread overlapping categorical points; numeric coordinates pass through.
  // The jitter seed folds in the point's groupKey so a multi-tag row (which
  // emits one render point per membership at identical coordinates) doesn't
  // stack its duplicates on top of each other.
  const jitteredRenderPoints = useMemo(
    () =>
      visibleRenderPoints.map((point) => {
        const jitterSeed = `${point.simulationOutputId}::${point.groupKey ?? ""}`
        return {
          ...point,
          x:
            xAxis.kind === "categorical"
              ? point.x + categoryJitter(jitterSeed, "x")
              : point.x,
          y:
            yAxis.kind === "categorical"
              ? point.y + categoryJitter(jitterSeed, "y")
              : point.y,
        }
      }),
    [visibleRenderPoints, xAxis.kind, yAxis.kind],
  )

  const xIsCategorical = xAxis.kind === "categorical"
  const yIsCategorical = yAxis.kind === "categorical"
  // Shade lanes on at most one axis; shading both crosshatches into noise.
  const shadeAxis: "x" | "y" | null = xIsCategorical
    ? "x"
    : yIsCategorical
      ? "y"
      : null

  // Least-squares trend line over the plotted (kept) points; excluded outliers
  // never enter the fit. Fed the analytics view so a multi-tag simulation
  // contributes to the fit exactly once.
  const trend = useMemo(() => {
    if (!config.showTrendLine) {
      return null
    }
    const fit = computeTrendLine(visibleAnalyticsPoints)
    if (!fit) {
      return null
    }
    let xMin = visibleAnalyticsPoints[0].x
    let xMax = visibleAnalyticsPoints[0].x
    for (const point of visibleAnalyticsPoints) {
      if (point.x < xMin) {
        xMin = point.x
      }
      if (point.x > xMax) {
        xMax = point.x
      }
    }
    return {
      segment: [
        { x: xMin, y: fit.slope * xMin + fit.intercept },
        { x: xMax, y: fit.slope * xMax + fit.intercept },
      ],
      r2: fit.r2,
    }
  }, [config.showTrendLine, visibleAnalyticsPoints])

  // The ReferenceLines unmount without firing onMouseLeave when the trend
  // disappears, so clear the hover flag to avoid a stale label on return.
  useEffect(() => {
    if (!trend) {
      setTrendHovered(false)
    }
  }, [trend])

  if (!xAxisMetricId || !yAxisMetricId) {
    return <EmptyState message="Select X and Y metrics to plot" />
  }
  if (analyticsPoints.length === 0) {
    return <EmptyState message="No simulations have values for both metrics" />
  }

  const xLabel = metricLabel(metricRecordsById, xAxisMetricId)
  const yLabel = metricLabel(metricRecordsById, yAxisMetricId)
  const zLabel = zAxisMetricId
    ? metricLabel(metricRecordsById, zAxisMetricId)
    : undefined

  const plotLabel = `${yLabel} against ${xLabel}`
  const figure = buildScatterFigure({
    points: visibleAnalyticsPoints,
    memberships: visibleRenderPoints,
    xLabel,
    yLabel,
    zLabel,
    grouped: Boolean(compareBy),
    trendR2: trend?.r2,
    onOpen: interactive ? onPointClick : undefined,
  })

  const handleClick = (
    node: ScatterClickNode,
    _index?: number,
    event?: { stopPropagation?: () => void },
  ) => {
    // Stop the point click from bubbling to the PlotCard's select handler.
    event?.stopPropagation?.()
    const id = node?.payload?.simulationOutputId
    if (id) {
      onPointClick(id)
    }
  }

  return (
    <div className="flex w-full flex-col">
      {/* The figure's buttons (table toggle, Open) act on the plot, not the
          PlotCard around it, so their clicks stop here as a mark click does. */}
      <div role="presentation" onClick={stopButtonClick}>
        <ChartFigure
          label={plotLabel}
          summary={figure.summary}
          table={figure.table}
          className="h-72 w-full [&_.recharts-symbols]:cursor-pointer"
        >
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart
              role="img"
              aria-label={plotLabel}
              margin={{ top: 16, right: 24, bottom: 28, left: 8 }}
            >
              <CartesianGrid
                stroke={DIVIDER_STROKE}
                strokeDasharray="3 3"
                vertical={!xIsCategorical}
                horizontal={!yIsCategorical}
              />
              {/* Faint alternating lanes so categories read as distinct columns. */}
              {shadeAxis === "x"
                ? alternatingBands(xAxis.categories.length).map((band) => (
                    <ReferenceArea
                      key={`x-band-${band.index}`}
                      x1={band.start}
                      x2={band.end}
                      fill={BAND_FILL}
                      fillOpacity={BAND_FILL_OPACITY}
                      stroke="none"
                      ifOverflow="hidden"
                    />
                  ))
                : null}
              {shadeAxis === "y"
                ? alternatingBands(yAxis.categories.length).map((band) => (
                    <ReferenceArea
                      key={`y-band-${band.index}`}
                      y1={band.start}
                      y2={band.end}
                      fill={BAND_FILL}
                      fillOpacity={BAND_FILL_OPACITY}
                      stroke="none"
                      ifOverflow="hidden"
                    />
                  ))
                : null}
              {/* Lane dividers on the boundaries between categories, not centers. */}
              {xIsCategorical
                ? categoryDividers(xAxis.categories.length).map((pos) => (
                    <ReferenceLine
                      key={`x-div-${pos}`}
                      x={pos}
                      stroke={DIVIDER_STROKE}
                    />
                  ))
                : null}
              {yIsCategorical
                ? categoryDividers(yAxis.categories.length).map((pos) => (
                    <ReferenceLine
                      key={`y-div-${pos}`}
                      y={pos}
                      stroke={DIVIDER_STROKE}
                    />
                  ))
                : null}
              <XAxis
                type="number"
                dataKey="x"
                name={xLabel}
                scale={xScale}
                tick={{ fontSize: 12 }}
                label={{
                  value: xLabel,
                  position: "insideBottom",
                  offset: -16,
                  fontSize: 12,
                }}
                {...categoricalAxisProps(xAxis)}
              />
              <YAxis
                type="number"
                dataKey="y"
                name={yLabel}
                scale={yScale}
                tick={{ fontSize: 12 }}
                label={{
                  value: yLabel,
                  angle: -90,
                  position: "insideLeft",
                  fontSize: 12,
                }}
                {...categoricalAxisProps(yAxis)}
              />
              {zAxisMetricId && (
                <ZAxis
                  type="number"
                  dataKey="z"
                  range={[40, 400]}
                  domain={zDomain}
                  scale={zScale}
                  name={zLabel}
                />
              )}
              <Tooltip
                cursor={{ strokeDasharray: "3 3" }}
                content={
                  <ComparisonTooltip
                    xLabel={xLabel}
                    yLabel={yLabel}
                    zLabel={zLabel}
                    interactive={interactive}
                  />
                }
              />
              {/* Dashed fit line — before <Scatter> so it plots under the points.
                Separate children, not a fragment (recharts drops fragment-wrapped
                chart children). */}
              {trend && (
                <ReferenceLine
                  segment={trend.segment}
                  stroke="var(--coval-text-muted)"
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  ifOverflow="extendDomain"
                />
              )}
              {/* Transparent wide hit-area, also under the points: drives the R²
                hover without blocking point clicks along the line. */}
              {trend && (
                <ReferenceLine
                  segment={trend.segment}
                  stroke="transparent"
                  strokeWidth={14}
                  ifOverflow="extendDomain"
                  onMouseEnter={() => setTrendHovered(true)}
                  onMouseLeave={() => setTrendHovered(false)}
                />
              )}
              <Scatter data={jitteredRenderPoints} onClick={handleClick}>
                {jitteredRenderPoints.map((point) => (
                  <Cell
                    key={`${point.simulationOutputId}::${point.groupKey ?? ""}`}
                    fill={point.color}
                  />
                ))}
              </Scatter>
              {/* Label-only line after <Scatter> so the R² paints over the points
                (recharts paints in child order); transparent line + pointer-events-none
                so nothing visible is added on top and points stay clickable. */}
              {trend && trendHovered && (
                <ReferenceLine
                  segment={trend.segment}
                  stroke="transparent"
                  ifOverflow="extendDomain"
                  className="pointer-events-none"
                  label={{
                    value: `R² = ${trend.r2.toFixed(2)}`,
                    position: "insideTop",
                    fill: "var(--coval-text-muted)",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                />
              )}
            </ScatterChart>
          </ResponsiveContainer>
        </ChartFigure>
      </div>
      {/* Card footer: legend left, point count right. The legend wraps to two
          lines, then scrolls; the config panel warns when it's likely to. */}
      <div className="flex items-start justify-between gap-4">
        {legendEntries.length > 0 && (
          <div className="flex max-h-9 min-w-0 flex-wrap items-center gap-x-3 gap-y-1 overflow-y-auto">
            {legendEntries.map((entry) => (
              <span
                key={entry.groupKey}
                className="flex items-center gap-1.5 text-xs text-coval-text-muted"
              >
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: entry.color }}
                  aria-hidden="true"
                />
                {entry.label}
              </span>
            ))}
          </div>
        )}
        <div className="ml-auto shrink-0 text-xs text-coval-text-muted">
          {formatPointCount(
            analyticsPoints.length,
            outlierIds.size,
            config.excludeOutliers ?? false,
          )}
        </div>
      </div>
    </div>
  )
}
